// src/features/kommunikation/services/DocumentParserService.ts

import { ParsedFilename } from '../types';
import { ParsedSonderversand } from '../types/upload.types';

/**
 * Service zum Parsen von Dateinamen im Format:
 * Mandantennummer_Personalnummer_Name_..._Monat_Jahr_Dokumententyp...
 * 
 * Beispiele:
 * - 42588_20069_Hofacker_Anna_Lisa_10_2025_Div_Auswertungen_R20
 * - 45290_00878_Nosko_Maryna_10_2025_Div_Auswertungen
 * - 45290_00871_Rus_Oleksandr_10_2025_Div_Auswertungen
 */
export class DocumentParserService {
  
  /**
   * Parst einen Dateinamen und extrahiert strukturierte Felder
   */
  parseFilename(filename: string): ParsedFilename {
    try {
      // Entferne .pdf Extension
      const nameWithoutExt = filename.replace(/\.pdf$/i, '');
      
      // Split nach Underscore
      const parts = nameWithoutExt.split('_');
      
      // Mindestens 5 Teile erforderlich: Mandant_Personal_Name_Monat_Jahr
      if (parts.length < 5) {
        return {
          mandantennummer: '',
          personalnummer: '',
          monat: 0,
          jahr: 0,
          dokumententyp: '',
          isValid: false,
          error: `Ungültiges Dateiformat. Erwartet mindestens 5 Teile, gefunden: ${parts.length}`
        };
      }
      
      // Parse sichere Positionen
      const mandantennummer = parts[0];
      
      // Personalnummer: Entferne führende Nullen
      // "00878" → "878", "20069" → "20069"
      const personalnummerRaw = parts[1];
      const personalnummer = parseInt(personalnummerRaw, 10).toString();
      
      // Suche nach Monat-Jahr-Kombination (ab Index 2)
      let monatIndex = -1;
      let jahrIndex = -1;
      
      for (let i = 2; i < parts.length - 1; i++) {
        const potentialMonth = parseInt(parts[i], 10);
        const potentialYear = parseInt(parts[i + 1], 10);
        
        // Prüfe ob gültige Monat-Jahr-Kombination
        if (!isNaN(potentialMonth) && 
            !isNaN(potentialYear) &&
            potentialMonth >= 1 && 
            potentialMonth <= 12 &&
            potentialYear >= 2020 && 
            potentialYear <= 2100) {
          monatIndex = i;
          jahrIndex = i + 1;
          break;
        }
      }
      
      // Validierung: Monat-Jahr gefunden?
      if (monatIndex === -1 || jahrIndex === -1) {
        return {
          mandantennummer,
          personalnummer,
          monat: 0,
          jahr: 0,
          dokumententyp: '',
          isValid: false,
          error: 'Keine gültige Monat-Jahr-Kombination gefunden (Format: 1-12 + 2020-2100)'
        };
      }
      
      const monat = parseInt(parts[monatIndex], 10);
      const jahr = parseInt(parts[jahrIndex], 10);
      
      // Dokumententyp = alles NACH dem Jahr
      const dokumententypParts = parts.slice(jahrIndex + 1);
      const dokumententyp = dokumententypParts.length > 0 
        ? dokumententypParts.join('_')
        : 'Unbekannt';
      
      // Validierung
      const errors: string[] = [];
      
      if (!mandantennummer || mandantennummer.length === 0) {
        errors.push('Mandantennummer fehlt');
      }
      
      if (!personalnummer || personalnummer.length === 0) {
        errors.push('Personalnummer fehlt');
      }
      
      if (isNaN(monat) || monat < 1 || monat > 12) {
        errors.push(`Ungültiger Monat: ${parts[monatIndex]}`);
      }
      
      if (isNaN(jahr) || jahr < 2020 || jahr > 2100) {
        errors.push(`Ungültiges Jahr: ${parts[jahrIndex]}`);
      }
      
      if (!dokumententyp || dokumententyp.length === 0) {
        errors.push('Dokumententyp fehlt');
      }
      
      if (errors.length > 0) {
        return {
          mandantennummer,
          personalnummer,
          monat: isNaN(monat) ? 0 : monat,
          jahr: isNaN(jahr) ? 0 : jahr,
          dokumententyp,
          isValid: false,
          error: errors.join(', ')
        };
      }
      
      return {
        mandantennummer,
        personalnummer,
        monat,
        jahr,
        dokumententyp,
        isValid: true
      };
      
    } catch (error) {
      return {
        mandantennummer: '',
        personalnummer: '',
        monat: 0,
        jahr: 0,
        dokumententyp: '',
        isValid: false,
        error: error instanceof Error ? error.message : 'Unbekannter Parsing-Fehler'
      };
    }
  }
  
  /**
   * NEU (Sonderversand): Parst einen Dateinamen lenient, ohne Monat/Jahr-Pflicht.
   *
   * Es werden ausschliesslich Mandantennummer (Position 1) und Personalnummer
   * (Position 2) benoetigt. Der Rest des Dateinamens wird als freie Beschreibung
   * uebernommen. Wird fuer den ausserplanmaessigen Versand verwendet, bei dem
   * Dokumente nicht dem Schema "Gehaltsunterlagen fuer Monat X" entsprechen.
   *
   * Erwartetes Mindestformat: Mandantennummer_Personalnummer_BeliebigerRest.pdf
   */
  parseFilenameSonderversand(filename: string): ParsedSonderversand {
    try {
      const nameWithoutExt = filename.replace(/\.pdf$/i, '');
      const parts = nameWithoutExt.split('_');
      
      if (parts.length < 2) {
        return {
          mandantennummer: '',
          personalnummer: '',
          beschreibung: '',
          isValid: false,
          error: `Ungültiges Dateiformat. Erwartet mindestens Mandantennummer_Personalnummer, gefunden: ${parts.length} Teil(e)`
        };
      }
      
      const mandantennummer = parts[0];
      const personalnummerRaw = parts[1];
      const personalnummerParsed = parseInt(personalnummerRaw, 10);
      const beschreibung = parts.slice(2).join('_');
      
      const errors: string[] = [];
      
      if (!mandantennummer || mandantennummer.length === 0) {
        errors.push('Mandantennummer fehlt');
      }
      
      if (isNaN(personalnummerParsed)) {
        errors.push(`Ungültige Personalnummer: ${personalnummerRaw}`);
      }
      
      // Personalnummer: Entferne führende Nullen (konsistent zu parseFilename)
      const personalnummer = isNaN(personalnummerParsed) ? '' : personalnummerParsed.toString();
      
      if (errors.length > 0) {
        return {
          mandantennummer,
          personalnummer,
          beschreibung,
          isValid: false,
          error: errors.join(', ')
        };
      }
      
      return {
        mandantennummer,
        personalnummer,
        beschreibung,
        isValid: true
      };
      
    } catch (error) {
      return {
        mandantennummer: '',
        personalnummer: '',
        beschreibung: '',
        isValid: false,
        error: error instanceof Error ? error.message : 'Unbekannter Parsing-Fehler'
      };
    }
  }
  
  /**
   * Validiert mehrere Dateinamen und gibt Statistiken zurück
   */
  validateBatch(filenames: string[]): {
    valid: ParsedFilename[];
    invalid: ParsedFilename[];
    stats: {
      total: number;
      valid: number;
      invalid: number;
    };
  } {
    const results = filenames.map(f => this.parseFilename(f));
    
    const valid = results.filter(r => r.isValid);
    const invalid = results.filter(r => !r.isValid);
    
    return {
      valid,
      invalid,
      stats: {
        total: results.length,
        valid: valid.length,
        invalid: invalid.length
      }
    };
  }
  
  /**
   * Prüft ob Dateiname grundsätzlich dem erwarteten Muster entspricht
   */
  looksValid(filename: string): boolean {
    const nameWithoutExt = filename.replace(/\.pdf$/i, '');
    const parts = nameWithoutExt.split('_');
    return parts.length >= 5;
  }
  
  /**
   * Formatiert einen Monatsnamen für Anzeige
   */
  formatMonthYear(monat: number, jahr: number): string {
    const monthNames = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    
    return `${monthNames[monat - 1]} ${jahr}`;
  }
}

export const documentParserService = new DocumentParserService();
